import { useQuery } from "urql";
import {
  PostSessionSurveyCareerQuestionDocument,
  Survey,
  SurveyQuestionTemplateId,
} from "../../generated/graphql";
import useRotatingQuestionIndex from "../questions/hooks/useRotatingQuestion";
import { SurveyAudienceContext } from "../survey_page";
import { getRotatingCareerOpenResponseQuestions } from "../questions/rotating_career_open_response";

export function useSurveyQuestion(
  question: Survey["questions"][0],
  surveyAudienceContext?: SurveyAudienceContext
) {
  const rotatingTemplatesIds = [
    SurveyQuestionTemplateId.RotatingOpenResponse,
    SurveyQuestionTemplateId.RotatingLikert,
    SurveyQuestionTemplateId.RotatingCareerOpenResponse,
  ];

  // Rotating career open response nonsense starts here //
  // This is necessary because unlike all other question types in the DB, the rotating career open response
  // question has hard-coded options in the FE.
  const questionIsCareerOpenResponse =
    question.template.templateId === SurveyQuestionTemplateId.RotatingCareerOpenResponse;

  const [{ fetching: fetchingSurveyAudienceEntities, data: surveyAudienceEntities }] = useQuery({
    query: PostSessionSurveyCareerQuestionDocument,
    variables: {
      participantId: surveyAudienceContext?.participantId ?? "",
      mentorshipId: surveyAudienceContext?.mentorshipId ?? "",
    },
    pause: !questionIsCareerOpenResponse || !surveyAudienceContext,
  });
  const careerRotatingQuestions =
    questionIsCareerOpenResponse && surveyAudienceEntities
      ? getRotatingCareerOpenResponseQuestions(
          surveyAudienceEntities?.participant,
          surveyAudienceEntities?.mentorship
        )
      : [];
  // Rotating career open response nonsense ends here /

  const numberOfRotatingQuestions =
    questionIsCareerOpenResponse && surveyAudienceEntities
      ? careerRotatingQuestions.length
      : question.customizations?.rotatingQuestions?.length;

  const { fetching: fetchingRotatingIndex, questionIndex } = useRotatingQuestionIndex({
    numberOfQuestions: numberOfRotatingQuestions,
    pause: !rotatingTemplatesIds.includes(question.template.templateId),
    surveyAudienceContext,
  });

  const getQuestionLabel = () => {
    switch (question.template.templateId) {
      case SurveyQuestionTemplateId.CounterpartsInfo:
      case SurveyQuestionTemplateId.DatePicker:
      case SurveyQuestionTemplateId.Flags:
      case SurveyQuestionTemplateId.Likert:
      case SurveyQuestionTemplateId.MentorshipChatGroupStudentSelect:
      case SurveyQuestionTemplateId.MultiSelect:
      case SurveyQuestionTemplateId.MultiSelectDropdown:
      case SurveyQuestionTemplateId.Nps:
      case SurveyQuestionTemplateId.NumberInput:
      case SurveyQuestionTemplateId.OpenResponse:
      case SurveyQuestionTemplateId.Select:
      case SurveyQuestionTemplateId.SelectWithSelectFollowUp: {
        return question.customizations.prompt;
      }
      case SurveyQuestionTemplateId.Generic: {
        return question.customizations.text;
      }
      case SurveyQuestionTemplateId.RotatingCareerOpenResponse: {
        return questionIndex !== undefined &&
          careerRotatingQuestions[questionIndex] !== undefined &&
          !fetchingRotatingIndex &&
          !fetchingSurveyAudienceEntities
          ? careerRotatingQuestions[questionIndex].prompt
          : "Question";
      }
      case SurveyQuestionTemplateId.RotatingLikert:
      case SurveyQuestionTemplateId.RotatingOpenResponse: {
        return fetchingRotatingIndex || questionIndex === undefined
          ? "Question"
          : question.customizations.rotatingQuestions[questionIndex].prompt;
      }
      case SurveyQuestionTemplateId.Conversation: {
        return (
          question.customizations.modeOfCommunicationPrompt ?? question.customizations.topicsPrompt
        );
      }
      case SurveyQuestionTemplateId.MentorshipChatGroupOptionalStudentInfo: {
        return question.customizations.yesNoPrompt;
      }
      default: {
        return question.customizations.prompt;
      }
    }
  };

  return {
    getQuestionLabel,
  };
}
